import type { JSX } from 'react';
import type { NomTheme } from '@artosera/site-renderer';

/**
 * Vignettes de thème (S6-f) : une page du site, réduite, avec ce qu'une
 * galerie y reconnaît — des œuvres encadrées et leur cartel. Chaque thème se
 * lit à sa mise en page (`packages/site-renderer/README.md`), en jetons de la
 * régie — ce ne sont pas des couleurs de marque, mais des traits d'interface
 * (D6).
 */

/** Une œuvre encadrée : cadre, marie-louise, et un motif peint. */
function Oeuvre({
  x,
  y,
  l,
  h,
  motif = 'horizon',
}: {
  x: number;
  y: number;
  l: number;
  h: number;
  motif?: 'horizon' | 'soleil' | 'plein';
}): JSX.Element {
  const marge = Math.max(2, Math.round(Math.min(l, h) * 0.08));
  const ix = x + marge;
  const iy = y + marge;
  const il = l - marge * 2;
  const ih = h - marge * 2;
  return (
    <g>
      <rect x={x} y={y} width={l} height={h} fill="var(--surface)" stroke="var(--line-strong)" />
      <rect x={ix} y={iy} width={il} height={ih} fill="var(--accent-soft)" />
      {motif === 'horizon' ? (
        <path
          d={`M${ix} ${iy + ih * 0.7} Q${ix + il * 0.35} ${iy + ih * 0.45} ${ix + il * 0.6} ${iy + ih * 0.62} T${ix + il} ${iy + ih * 0.55} V${iy + ih} H${ix} Z`}
          fill="var(--accent)"
          opacity="0.35"
        />
      ) : null}
      {motif === 'soleil' ? (
        <circle
          cx={ix + il * 0.62}
          cy={iy + ih * 0.4}
          r={Math.min(il, ih) * 0.22}
          fill="var(--accent)"
          opacity="0.4"
        />
      ) : null}
      {motif === 'plein' ? (
        <rect
          x={ix + il * 0.18}
          y={iy + ih * 0.2}
          width={il * 0.42}
          height={ih * 0.6}
          fill="var(--accent)"
          opacity="0.3"
        />
      ) : null}
    </g>
  );
}

/** Le cartel sous une œuvre : nom (trait foncé), titre et technique (traits pâles). */
function Cartel({ x, y, l }: { x: number; y: number; l: number }): JSX.Element {
  return (
    <g>
      <rect x={x} y={y} width={l * 0.7} height="3" rx="1" fill="var(--ink-soft)" />
      <rect x={x} y={y + 6} width={l * 0.5} height="2" rx="1" fill="var(--ink-faint)" />
    </g>
  );
}

/** Barre de tête du site : logotype et trois rubriques. */
function Tete({ fond = 'var(--surface)' }: { fond?: string }): JSX.Element {
  return (
    <g>
      <rect width="160" height="16" fill={fond} />
      <rect x="10" y="6" width="22" height="4" rx="1" fill="var(--ink)" />
      <rect x="104" y="7" width="12" height="2" rx="1" fill="var(--ink-faint)" />
      <rect x="121" y="7" width="12" height="2" rx="1" fill="var(--ink-faint)" />
      <rect x="138" y="7" width="12" height="2" rx="1" fill="var(--ink-faint)" />
    </g>
  );
}

const VIGNETTES: Record<NomTheme, (props: { titre: string }) => JSX.Element> = {
  // Le cube blanc : trois œuvres alignées sur un mur vide, chacune son cartel.
  cimaise: ({ titre }) => (
    <svg viewBox="0 0 160 120" role="img" aria-label={titre}>
      <rect width="160" height="120" fill="var(--surface)" />
      <Tete />
      <rect x="0" y="16" width="160" height="1" fill="var(--line)" />
      <Oeuvre x={12} y={38} l={38} h={44} motif="horizon" />
      <Oeuvre x={61} y={38} l={38} h={44} motif="soleil" />
      <Oeuvre x={110} y={38} l={38} h={44} motif="plein" />
      <Cartel x={12} y={90} l={38} />
      <Cartel x={61} y={90} l={38} />
      <Cartel x={110} y={90} l={38} />
    </svg>
  ),
  // Le cartel de musée : un titre éditorial, du texte, une seule œuvre.
  cartel: ({ titre }) => (
    <svg viewBox="0 0 160 120" role="img" aria-label={titre}>
      <rect width="160" height="120" fill="var(--surface)" />
      <Tete />
      <rect x="0" y="16" width="160" height="1" fill="var(--line)" />
      <rect x="14" y="30" width="62" height="7" rx="1" fill="var(--ink)" />
      <rect x="14" y="41" width="44" height="7" rx="1" fill="var(--ink)" />
      <rect x="14" y="58" width="58" height="2" rx="1" fill="var(--ink-faint)" />
      <rect x="14" y="64" width="58" height="2" rx="1" fill="var(--ink-faint)" />
      <rect x="14" y="70" width="58" height="2" rx="1" fill="var(--ink-faint)" />
      <rect x="14" y="76" width="40" height="2" rx="1" fill="var(--ink-faint)" />
      <rect x="14" y="88" width="58" height="2" rx="1" fill="var(--ink-faint)" />
      <rect x="14" y="94" width="50" height="2" rx="1" fill="var(--ink-faint)" />
      <Oeuvre x={94} y={28} l={52} h={66} motif="soleil" />
      <Cartel x={94} y={100} l={52} />
    </svg>
  ),
  // L'accrochage au mur : l'image d'abord, pleine largeur, puis la grille.
  accrochage: ({ titre }) => (
    <svg viewBox="0 0 160 120" role="img" aria-label={titre}>
      <rect width="160" height="120" fill="var(--sunken)" />
      <rect x="0" y="16" width="160" height="62" fill="var(--accent-soft)" />
      <path d="M0 62 Q40 44 72 56 T160 50 V78 H0 Z" fill="var(--accent)" opacity="0.35" />
      <rect x="12" y="64" width="40" height="5" rx="1" fill="var(--surface)" />
      <Tete />
      <rect x="0" y="80" width="79" height="40" fill="var(--accent-soft)" />
      <circle cx="44" cy="96" r="9" fill="var(--accent)" opacity="0.4" />
      <rect x="81" y="80" width="79" height="40" fill="var(--accent-soft)" />
      <rect x="98" y="88" width="22" height="26" fill="var(--accent)" opacity="0.3" />
    </svg>
  ),
};

export function VignetteTheme({ theme, titre }: { theme: NomTheme; titre: string }): JSX.Element {
  const Vignette = VIGNETTES[theme];
  return <Vignette titre={titre} />;
}
